import { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  useTheme,
  alpha,
  Divider,
  Paper,
  Tabs,
  Tab,
  Button,
  ButtonGroup,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import NatureIcon from '@mui/icons-material/Nature';
import ParkIcon from '@mui/icons-material/Park';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import BatteryChargingFullIcon from '@mui/icons-material/BatteryChargingFull';
import DownloadIcon from '@mui/icons-material/Download';
import InsightsIcon from '@mui/icons-material/Insights';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';

// Chart components
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
  Legend,
} from 'recharts';

// Import from our business service
import { 
  getBusiness, 
  getCarbonImpact
} from '../services/business';

import { 
  Business, 
  CarbonImpactData
} from '../services/business';

interface CarbonImpactProps {
  businessId: string;
}

const CarbonImpact = ({ businessId }: CarbonImpactProps) => {
  const theme = useTheme();
  
  // States for data
  const [business, setBusiness] = useState<Business | null>(null);
  const [carbonImpact, setCarbonImpact] = useState<CarbonImpactData | null>(null);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [timeFrame, setTimeFrame] = useState(-1);  // -1 means ALL data
  const [graphType, setGraphType] = useState(0);  // 0 = cumulative, 1 = monthly

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch business info
        const businessData = await getBusiness(businessId);
        setBusiness(businessData);
        
        // Fetch carbon impact data
        const impactData = await getCarbonImpact(businessId);
        setCarbonImpact(impactData);
        
        setError('');
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Error loading data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [businessId]);

  // Handle tab change
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Loading state
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography color="error" variant="h6">{error}</Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Carbon Impact Metrics
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Track the positive environmental impact of your renewable energy usage
        </Typography>
      </Box>
      
      {/* Key Metrics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* CO2 Emissions Avoided */}
        <Grid item xs={12} md={6}>
          <Card 
            elevation={0} 
            sx={{ 
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box 
              sx={{ 
                position: 'absolute',
                top: 0,
                right: 0,
                width: '150px',
                height: '150px',
                borderRadius: '0 0 0 150px',
                bgcolor: alpha(theme.palette.success.main, 0.08),
                zIndex: 0
              }}
            />
            
            <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  CO₂ Emissions Avoided
                </Typography>
                <NatureIcon 
                  sx={{ 
                    color: theme.palette.success.main, 
                    fontSize: '2rem' 
                  }} 
                />
              </Box>
              
              <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 1 }}>
                <Typography variant="h2" sx={{ fontWeight: 700 }}>
                  {carbonImpact ? carbonImpact.totalCO2Avoided.toLocaleString() : '0'}
                </Typography>
                <Typography variant="h5" sx={{ ml: 1, fontWeight: 500, color: 'text.secondary' }}>
                  lbs
                </Typography>
              </Box>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                {business?.renewablePercentage}% renewable energy as a GREEN P100NEER since {carbonImpact?.period?.start || 'January 2023'}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Box sx={{ height: 240 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart
                    width={400}
                    height={300}
                  >
                    <Pie
                      data={carbonImpact?.yearlyData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={80}
                      fill={theme.palette.success.main}
                      dataKey="co2Avoided"
                    >
                      <Cell fill={alpha(theme.palette.success.main, 0.2)} />
                    </Pie>
                    <Tooltip 
                      labelStyle={{ color: 'black' }}
                      itemStyle={{ color: 'black' }}
                      labelFormatter={(value: number) => `${value.toLocaleString()} lbs`}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 1 }}>
                Monthly CO₂ emissions avoided over the past year
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        {/* Environmental Equivalents */}
        <Grid item xs={12} md={6}>
          <Card 
            elevation={0} 
            sx={{ 
              borderRadius: 4,
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              height: '100%',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                Environmental Equivalents
              </Typography>
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Your CO₂ reduction is equivalent to:
              </Typography>
              
              <Grid container spacing={3}>
                {/* Trees Planted */}
                <Grid item xs={12} sm={6}>
                  <Box 
                    sx={{ 
                      p: 2, 
                      bgcolor: alpha(theme.palette.success.main, 0.05),
                      borderRadius: 3,
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <ParkIcon sx={{ color: theme.palette.success.main, mr: 1.5, fontSize: '1.75rem' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Trees Planted
                      </Typography>
                    </Box>
                    
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      {carbonImpact?.treesEquivalent.toLocaleString()}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary">
                      Trees seedlings grown for 10 years
                    </Typography>
                  </Box>
                </Grid>
                
                {/* Car Miles */}
                <Grid item xs={12} sm={6}>
                  <Box 
                    sx={{ 
                      p: 2, 
                      bgcolor: alpha(theme.palette.info.main, 0.05),
                      borderRadius: 3,
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <DirectionsCarIcon sx={{ color: theme.palette.info.main, mr: 1.5, fontSize: '1.75rem' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Car Miles
                      </Typography>
                    </Box>
                    
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      {carbonImpact?.carMilesEquivalent.toLocaleString()}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary">
                      Car miles not driven
                    </Typography>
                  </Box>
                </Grid>
                
                {/* Coal Saved */}
                <Grid item xs={12} sm={6}>
                  <Box 
                    sx={{ 
                      p: 2, 
                      bgcolor: alpha(theme.palette.grey[700], 0.05),
                      borderRadius: 3,
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <BubbleChartIcon sx={{ color: theme.palette.grey[700], mr: 1.5, fontSize: '1.75rem' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Coal Saved
                      </Typography>
                    </Box>
                    
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      {carbonImpact?.coalSaved?.toLocaleString() || '0'} lbs
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary">
                      Pounds of coal not burned
                    </Typography>
                  </Box>
                </Grid>
                
                {/* Smartphones Charged */}
                <Grid item xs={12} sm={6}>
                  <Box 
                    sx={{ 
                      p: 2, 
                      bgcolor: alpha(theme.palette.secondary.main, 0.05),
                      borderRadius: 3,
                      height: '100%',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                      <BatteryChargingFullIcon sx={{ color: theme.palette.secondary.main, mr: 1.5, fontSize: '1.75rem' }} />
                      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                        Smartphones
                      </Typography>
                    </Box>
                    
                    <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
                      {((carbonImpact?.smartphonesCharged ?? 0) / 1000000).toFixed(1)}M
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary">
                      Smartphones charged
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      
      {/* Additional Information */}
      <Paper elevation={0} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)', mb: 4 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="fullWidth"
            sx={{
              '& .MuiTab-root': {
                fontWeight: 600,
                py: 2,
              }
            }}
          >
            <Tab 
              label="Impact Over Time" 
              icon={<InsightsIcon />} 
              iconPosition="start"
            />
            <Tab 
              label="Impact Details" 
              icon={<EmojiEventsIcon />} 
              iconPosition="start"
            />
          </Tabs>
        </Box>
        
        <Box sx={{ p: 3 }}>
          {/* Impact Over Time Tab */}
          {tabValue === 0 && (
            <Box>
              <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Tabs 
                  value={timeFrame === -1 ? 0 : timeFrame === 12 ? 1 : timeFrame === 6 ? 2 : 3}
                  onChange={(_, value) => {
                    setTimeFrame(value === 0 ? -1 : value === 1 ? 12 : value === 2 ? 6 : 3);
                  }}
                >
                  <Tab label="ALL" />
                  <Tab label="12 Months" />
                  <Tab label="6 Months" />
                  <Tab label="3 Months" />
                </Tabs>
                
                <Tabs 
                  value={graphType}
                  onChange={(_, value) => setGraphType(value)}
                >
                  <Tab label="Cumulative" />
                  <Tab label="Monthly" />
                </Tabs>
              </Box>
              
              {/* Cumulative CO2 Savings Area Chart */}
              {graphType === 0 && (
                <Box sx={{ height: 400, mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Cumulative CO₂ Savings
                  </Typography>
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={calculateCumulativeData(
                        timeFrame === -1 
                          ? carbonImpact?.yearlyData || [] 
                          : carbonImpact?.yearlyData?.slice(-timeFrame) || []
                      )}
                      margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.1)} />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis 
                        label={{ 
                          value: 'Total CO₂ Avoided (lbs)', 
                          angle: -90, 
                          position: 'insideLeft',
                          style: { textAnchor: 'middle' }
                        }}
                      />
                      <Tooltip 
                        formatter={(value: number) => [`${value.toLocaleString()} lbs`, 'Cumulative CO₂ Avoided']}
                      />
                      <Area
                        type="monotone"
                        dataKey="cumulativeCO2"
                        name="Cumulative CO₂ Avoided"
                        stroke={theme.palette.success.main}
                        fill={alpha(theme.palette.success.main, 0.2)}
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              )}
              
              {/* Monthly CO2 Avoided Bar Chart */}
              {graphType === 1 && (
                <Box sx={{ height: 400, mb: 3 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Monthly CO₂ Emissions Avoided
                  </Typography>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={
                        timeFrame === -1 
                          ? carbonImpact?.yearlyData || [] 
                          : carbonImpact?.yearlyData?.slice(-timeFrame) || []
                      }
                      margin={{ top: 20, right: 30, left: 20, bottom: 50 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke={alpha('#000', 0.1)} />
                      <XAxis 
                        dataKey="month" 
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={60}
                      />
                      <YAxis 
                        label={{ 
                          value: 'CO₂ Avoided (lbs)', 
                          angle: -90, 
                          position: 'insideLeft',
                          style: { textAnchor: 'middle' }
                        }} 
                      />
                      <Tooltip formatter={(value: number) => [`${value.toLocaleString()} lbs`, 'CO₂ Avoided']} />
                      <Bar 
                        dataKey="co2Avoided" 
                        name="CO₂ Avoided" 
                        fill={theme.palette.success.main}
                        radius={[4, 4, 0, 0]}
                        barSize={30}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              )}
            </Box>
          )}
          
          {/* Impact Details Tab */}
          {tabValue === 1 && (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Understanding Your Carbon Impact
              </Typography>

              {/* Section 1: Calculation Methodology */}
              <Paper elevation={0} sx={{ p: 3, mb: 4, bgcolor: 'grey.100', borderRadius: 2 }}>
                <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600, color: 'primary.main' }}>
                  CO₂ Avoided Calculation Basis
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: 'text.secondary' }}>
                  Your positive carbon impact stems from using 100% renewable energy. We quantify this benefit by comparing your clean energy usage to the emissions generated by the standard regional electricity grid (often supplied by Investor-Owned Utilities). This standard grid mix typically includes a significant portion of fossil fuels (for example, potentially only around 37% renewable sources).
                </Typography>
                <Typography variant="body1" paragraph sx={{ color: 'text.secondary' }}>
                  Our calculations align with established methodologies from the U.S. Environmental Protection Agency (EPA). We utilize EPA-published emission factors (such as those found in the eGRID database) to estimate the amount of CO₂ emissions avoided by displacing conventional grid power with your renewable energy consumption.
                </Typography>
                {/* IMPORTANT: Do NOT display the specific formulas here */}
                <Link
                  href="https://www.epa.gov/egrid" // Update with a more specific link if available
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="body2"
                  sx={{ fontWeight: 500 }}
                >
                  Learn more about EPA eGRID methodology
                </Link>
              </Paper>

              {/* Section 2: Improving Impact */}
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 600 }}>
                Ways to Enhance Your Positive Impact
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Beyond using renewable energy, consider these strategies:
              </Typography>
              <List dense>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {/* Optional: Add an appropriate icon */}
                    {/* <EnergySavingsLeafOutlinedIcon color="success" /> */}
                  </ListItemIcon>
                  <ListItemText
                    primary="Improve Energy Efficiency"
                    secondary="Implement energy-saving practices in lighting, HVAC, and equipment usage."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {/* <ScheduleSendOutlinedIcon color="info" /> */}
                  </ListItemIcon>
                  <ListItemText
                    primary="Optimize Peak Load"
                    secondary="Adjust high-demand operations to off-peak hours when grid strain (and often emissions) are lower."
                  />
                </ListItem>
                <ListItem>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {/* <ShareOutlinedIcon color="secondary" /> */}
                  </ListItemIcon>
                  <ListItemText
                    primary="Engage Your Community"
                    secondary="Share your commitment to sustainability and inspire others."
                  />
                </ListItem>
              </List>
            </Box>
          )}
        </Box>
      </Paper>
      
      {/* Call to Action */}
      <Card 
        elevation={0} 
        sx={{ 
          borderRadius: 4,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.success.main, 0.9)})`,
          color: '#ffffff',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={8}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                Amplify Your Environmental Impact
              </Typography>
              <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>
                {business?.renewablePercentage === 100
                  ? "You're already using 100% renewable energy! Consider sharing your achievement with others to inspire more businesses to follow your lead."
                  : `Increasing your renewable energy percentage to 100% could boost your environmental impact by up to ${Math.round((100 - (business?.renewablePercentage || 0)) * 1.2)}%.`
                }
              </Typography>
              <Button 
                variant="contained" 
                color="inherit"
                sx={{ 
                  bgcolor: '#ffffff',
                  color: theme.palette.primary.main,
                  fontWeight: 600,
                  '&:hover': {
                    bgcolor: alpha('#ffffff', 0.9),
                  }
                }}
              >
                {business?.renewablePercentage === 100
                  ? "Share Your Achievement"
                  : "Explore 100% Renewable Options"
                }
              </Button>
            </Grid>
            <Grid item xs={12} md={4} sx={{ display: 'flex', justifyContent: 'center' }}>
              <Box 
                sx={{ 
                  position: 'relative', 
                  width: 180, 
                  height: 180,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: 0, 
                    left: 0, 
                    width: '100%', 
                    height: '100%',
                    borderRadius: '50%',
                    background: `conic-gradient(rgba(255,255,255,0.9) ${business?.renewablePercentage || 0}%, rgba(255,255,255,0.3) 0%)`,
                  }}
                />
                <Box 
                  sx={{ 
                    position: 'absolute', 
                    top: 10, 
                    left: 10, 
                    width: 'calc(100% - 20px)', 
                    height: 'calc(100% - 20px)',
                    borderRadius: '50%',
                    background: `linear-gradient(to right, ${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.success.main, 0.9)})`,
                  }}
                />
                <Box 
                  sx={{ 
                    position: 'relative', 
                    textAlign: 'center',
                    zIndex: 1,
                  }}
                >
                  <Typography variant="h3" sx={{ fontWeight: 800, color: '#ffffff' }}>
                    {business?.renewablePercentage}%
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#ffffff', opacity: 0.9 }}>
                    Renewable
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

// Helper function to calculate cumulative data for area chart
const calculateCumulativeData = (monthlyData: { month: string; co2Avoided: number; yearMonth: string }[]) => {
  let cumulativeTotal = 0;
  return monthlyData.map(item => {
    cumulativeTotal += item.co2Avoided;
    return {
      ...item,
      cumulativeCO2: cumulativeTotal
    };
  });
};

export default CarbonImpact; 